import { useCallback, useEffect, useMemo } from "react";

import { setAnalyticsUserProperties } from "@/constants/amplitude";
import {
    getLanguage,
    Languages,
    languagesStrings,
    mapLocalizedLanguage,
} from "@/constants/languages";
import i18next from "@/localization/i18next";

/** Dedupes analytics: this hook runs in many components; each mount would otherwise re-set the same user property. */
let lastWebFunnelLanguageSent: string | undefined;

export const useLocalization = () => {
    const targetLanguage = useMemo(() => getLanguage(i18next.language), []);

    const getIsLang = useCallback(
        (lng: Languages) => !!targetLanguage && targetLanguage.includes(lng),
        [targetLanguage]
    );

    const isSpanish = useMemo(() => getIsLang(Languages.Spanish), [getIsLang]);
    const isEnglish = useMemo(() => getIsLang(Languages.English), [getIsLang]);
    const isGerman = useMemo(() => getIsLang(Languages.German), [getIsLang]);
    const isPortuguese = useMemo(() => getIsLang(Languages.Portuguese), [getIsLang]);
    const isFrench = useMemo(() => getIsLang(Languages.French), [getIsLang]);

    useEffect(() => {
        if (!targetLanguage) {
            return;
        }
        const value = languagesStrings[targetLanguage];
        if (value === lastWebFunnelLanguageSent) {
            return;
        }
        lastWebFunnelLanguageSent = value;
        setAnalyticsUserProperties({
            web_funnel_language: value,
        });
    }, [targetLanguage]);

    // for now we can send only en, es, de, pt to backend
    const backendLanguage = useMemo(
        () =>
            [Languages.English, Languages.Spanish, Languages.German, Languages.Portuguese].includes(
                targetLanguage
            )
                ? targetLanguage
                : Languages.English,
        [targetLanguage]
    );

    return {
        isSpanish,
        isEnglish,
        isGerman,
        isPortuguese,
        isFrench,
        language: targetLanguage,
        localizedLanguage: mapLocalizedLanguage[targetLanguage],
        backendLanguage,
    };
};
